import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { inboundRoutesApi, trunksApi } from '../services/api'
import { DestinationSelector } from '../components/common'
import { useConfirm } from '../components/common/ConfirmDialog'

interface InboundRoute {
  id: string
  name: string
  description: string
  did_pattern: string
  caller_id_pattern: string
  caller_id_name_pattern: string
  trunk_id: string
  destination_type: string
  destination_id: string
  alert_info: string
  ringtone: string
  cid_lookup_source: string
  privacy_manager: boolean
  priority: number
  is_active: boolean
}

// Available ringtones (same as Yealink default)
const ringtones = [
  'Ring1.wav', 'Ring2.wav', 'Ring3.wav', 'Ring4.wav', 'Ring5.wav',
  'Ring6.wav', 'Ring7.wav', 'Ring8.wav', 'Ring9.wav', 'Ring10.wav',
  'Silent.wav'
]

const emptyForm = {
  name: '',
  description: '',
  did_pattern: '',
  caller_id_pattern: '',
  caller_id_name_pattern: '',
  trunk_id: '',
  destination_type: '',
  destination_id: '',
  alert_info: '',
  ringtone: '',
  cid_lookup_source: '',
  privacy_manager: false,
  priority: 100,
  is_active: true
}

// Allowed destination types for inbound routes
const INBOUND_ALLOWED_TYPES = [
  'extension',
  'ring_group',
  'queue',
  'ivr',
  'time_condition',
  'conference',
  'voicemail',
  'fax',
  'parking',
  'night_mode',
  'announcement',
  'external_dest',
  'external',
  'hangup',
]

export default function InboundRoutes() {
  const confirm = useConfirm()
  const [routes, setRoutes] = useState<InboundRoute[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingRoute, setEditingRoute] = useState<InboundRoute | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [trunks, setTrunks] = useState<{id: string, name: string}[]>([])

  useEffect(() => {
    loadRoutes()
    loadTrunks()
  }, [])

  const loadRoutes = async () => {
    try {
      const response = await inboundRoutesApi.list()
      setRoutes(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadTrunks = async () => {
    try {
      const response = await trunksApi.list()
      setTrunks(response.data.items || [])
    } catch {
      console.error('Error loading trunks')
    }
  }

  const openCreateModal = () => {
    setEditingRoute(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (route: InboundRoute) => {
    setEditingRoute(route)
    setFormData({
      name: route.name,
      description: route.description || '',
      did_pattern: route.did_pattern || '',
      caller_id_pattern: route.caller_id_pattern || '',
      caller_id_name_pattern: route.caller_id_name_pattern || '',
      trunk_id: route.trunk_id || '',
      destination_type: route.destination_type || '',
      destination_id: route.destination_id || '',
      alert_info: route.alert_info || '',
      ringtone: route.ringtone || '',
      cid_lookup_source: route.cid_lookup_source || '',
      privacy_manager: route.privacy_manager ?? false,
      priority: route.priority || 100,
      is_active: route.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData: Record<string, unknown> = { ...formData }
      if (!submitData.trunk_id) delete submitData.trunk_id
      if (!formData.did_pattern || !formData.did_pattern.trim()) {
        toast.error('Bitte DID-Muster eingeben')
        return
      }
      if (!submitData.destination_type) {
        toast.error('Bitte wählen Sie ein Ziel aus')
        return
      }
      if (!submitData.destination_id && formData.destination_type.toUpperCase() !== 'HANGUP') {
        toast.error('Bitte wählen Sie ein Ziel aus')
        return
      }

      if (editingRoute) {
        await inboundRoutesApi.update(editingRoute.id, submitData)
        toast.success('Route aktualisiert')
      } else {
        await inboundRoutesApi.create(submitData)
        toast.success('Route erstellt')
      }
      closeModal()
      loadRoutes()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (route: InboundRoute) => {
    const confirmed = await confirm({
      title: 'Route löschen',
      message: `Route "${route.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await inboundRoutesApi.delete(route.id)
      toast.success('Route gelöscht')
      loadRoutes()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingRoute(null)
    setFormData(emptyForm)
  }

  const getDestinationDisplay = (route: InboundRoute) => {
    if (!route.destination_type) return '-'
    if (route.destination_type.toUpperCase() === 'HANGUP') return 'Auflegen'
    return route.destination_id || '-'
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Eingehende Routen</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Route
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Priorität</th>
              <th>Name</th>
              <th>DID-Muster</th>
              <th>Anrufer-ID</th>
              <th>Ziel</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-4">Laden...</td></tr>
            ) : routes.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-4 text-muted">Keine Routen</td></tr>
            ) : (
              routes.map(r => (
                <tr key={r.id}>
                  <td><Badge bg="secondary">{r.priority}</Badge></td>
                  <td><strong>{r.name}</strong></td>
                  <td>{r.did_pattern || <span className="text-muted">*</span>}</td>
                  <td>{r.caller_id_pattern || <span className="text-muted">*</span>}</td>
                  <td><Badge bg="info">{getDestinationDisplay(r)}</Badge></td>
                  <td><Badge bg={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(r)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(r)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingRoute ? 'Route bearbeiten' : 'Neue Route'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Hauptnummer"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priorität</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 100})}
                    min={1}
                    max={9999}
                  />
                  <Form.Text className="text-muted">Niedriger = höhere Priorität</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optionale Beschreibung"
              />
            </Form.Group>

            <Card className="mb-3">
              <Card.Header><i className="bi bi-funnel me-2"></i>Anruf-Matching</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>DID-Muster (Angerufene Nummer) <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        value={formData.did_pattern}
                        onChange={(e) => setFormData({...formData, did_pattern: e.target.value})}
                        placeholder="z.B. 0041625179965"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Trunk</Form.Label>
                      <Form.Select
                        value={formData.trunk_id}
                        onChange={(e) => setFormData({...formData, trunk_id: e.target.value})}
                      >
                        <option value="">-- Alle Trunks --</option>
                        {trunks.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anrufer-ID Muster</Form.Label>
                      <Form.Control
                        value={formData.caller_id_pattern}
                        onChange={(e) => setFormData({...formData, caller_id_pattern: e.target.value})}
                        placeholder="z.B. +49*, _X."
                      />
                      <Form.Text className="text-muted">Leer = alle Anrufer</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anrufer-Name Muster</Form.Label>
                      <Form.Control
                        value={formData.caller_id_name_pattern}
                        onChange={(e) => setFormData({...formData, caller_id_name_pattern: e.target.value})}
                        placeholder="z.B. VIP*"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header><i className="bi bi-signpost-2 me-2"></i>Ziel</Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Ziel *</Form.Label>
                  <DestinationSelector
                    destinationType={formData.destination_type}
                    destinationValue={formData.destination_id}
                    onChange={(type, value) => setFormData({
                      ...formData,
                      destination_type: type,
                      destination_id: value
                    })}
                    allowedTypes={INBOUND_ALLOWED_TYPES}
                    allowExternal={true}
                    placeholder="Ziel suchen..."
                    required
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header><i className="bi bi-gear me-2"></i>Erweiterte Optionen</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Klingelton</Form.Label>
                      <Form.Select
                        value={formData.ringtone}
                        onChange={(e) => setFormData({...formData, ringtone: e.target.value})}
                      >
                        <option value="">Standard (extern)</option>
                        {ringtones.map(r => <option key={r} value={r}>{r}</option>)}
                      </Form.Select>
                      <Form.Text className="text-muted">Spezieller Klingelton für diese Route</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anrufername aus Telefonbuch</Form.Label>
                      <Form.Select
                        value={formData.cid_lookup_source}
                        onChange={(e) => setFormData({...formData, cid_lookup_source: e.target.value})}
                      >
                        <option value="">Kein Lookup (Anrufername vom Provider)</option>
                        <option value="phonebook">Telefonbuch durchsuchen</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Bei "Telefonbuch durchsuchen" wird der Anrufername aus dem internen Telefonbuch ermittelt
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="privacy_manager"
                        label="Privacy Manager aktivieren"
                        checked={formData.privacy_manager}
                        onChange={(e) => setFormData({...formData, privacy_manager: e.target.checked})}
                      />
                      <Form.Text className="text-muted">Anonyme Anrufer zur Identifikation auffordern</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="is_active"
                        label="Aktiv"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingRoute ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
